/**
 * WorkflowTracker Component
 * Shows active DV Agent workflow execution with step progress.
 * Displays a panel similar to TaskTracker but for multi-step workflows.
 */
import React, { useState, useEffect, useRef } from 'react';
import { GitBranch, ChevronDown, ChevronUp, X, CheckCircle, AlertCircle, StopCircle, RefreshCw } from 'lucide-react';
import './WorkflowTracker.css';

const WorkflowTracker = ({ workflow, onClear, onStop }) => {
    const [isOpen, setIsOpen] = useState(true);
    const dropdownRef = useRef(null);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    if (!workflow) {
        return null;
    }

    const { name, steps, currentStep, status, stepProgress } = workflow;

    const completedSteps = currentStep !== null ? currentStep : (status === 'completed' ? steps.length : 0);
    const totalSteps = steps.length;
    const progressPercent = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

    const isComplete = status === 'completed';
    const isStopped = status === 'stopped' || status === 'error';
    const isRunning = status === 'running';

    return (
        <div className="workflow-tracker" ref={dropdownRef}>
            {/* Toggle Button */}
            <button
                className={`workflow-tracker-btn ${isOpen ? 'active' : ''} ${isComplete ? 'completed' : isStopped ? 'stopped' : 'running'}`}
                onClick={() => setIsOpen(!isOpen)}
                title="DV Agent Workflow"
            >
                {isComplete ? (
                    <CheckCircle size={16} />
                ) : isStopped ? (
                    <StopCircle size={16} />
                ) : (
                    <GitBranch size={16} className="spinning" />
                )}
                <span className="workflow-label">
                    {isComplete ? 'Workflow Complete' : isStopped ? 'Workflow Stopped' : name || 'Running Workflow'}
                </span>
                <span className="workflow-count">
                    {completedSteps}/{totalSteps} steps
                </span>
                {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>

            {/* Dropdown Panel */}
            {isOpen && (
                <div className="workflow-dropdown">
                    <div className="workflow-dropdown-header">
                        <span>
                            <GitBranch size={14} />
                            Workflow Progress
                        </span>
                    </div>

                    <div className="workflow-content">
                        {/* Overall Progress Bar */}
                        <div className="workflow-overall-progress">
                            <div className="workflow-progress-header">
                                <span className="workflow-name">{name}</span>
                                <span className="workflow-stats">
                                    {completedSteps} / {totalSteps} steps
                                </span>
                            </div>
                            <div className="workflow-progress-bar-container">
                                <div
                                    className={`workflow-progress-bar ${isComplete ? 'completed' : isStopped ? 'stopped' : ''}`}
                                    style={{ width: `${progressPercent}%` }}
                                />
                            </div>
                        </div>

                        {/* Step List */}
                        <div className="workflow-steps-list">
                            {steps.map((step, idx) => {
                                const isCurrentStep = currentStep === idx;
                                const isCompletedStep = currentStep !== null ? idx < currentStep : isComplete;
                                const isPendingStep = currentStep !== null ? idx > currentStep : !isComplete && !isStopped;

                                return (
                                    <div
                                        key={step.id}
                                        className={`workflow-step-item ${isCurrentStep ? 'running' : ''} ${isCompletedStep ? 'completed' : ''} ${isPendingStep ? 'pending' : ''}`}
                                    >
                                        <div className="step-status-indicator">
                                            {isCompletedStep ? (
                                                <CheckCircle size={16} className="step-icon completed" />
                                            ) : isCurrentStep ? (
                                                <RefreshCw size={16} className="step-icon spinning" />
                                            ) : (
                                                <div className="step-number">{idx + 1}</div>
                                            )}
                                        </div>
                                        <div className="step-info">
                                            <span className="step-name">{step.name}</span>
                                            <span className="step-tasks-count">
                                                {step.parallel_tasks.length} task{step.parallel_tasks.length !== 1 ? 's' : ''}
                                            </span>
                                        </div>

                                        {/* Task progress when this is the current step */}
                                        {isCurrentStep && stepProgress && Object.keys(stepProgress).length > 0 && (
                                            <div className="step-task-progress">
                                                {Object.entries(stepProgress).map(([taskId, progress]) => (
                                                    <div key={taskId} className="task-progress-item">
                                                        <span className="task-name">{progress.name}</span>
                                                        <div className="task-progress-bar">
                                                            <div
                                                                className="task-progress-fill"
                                                                style={{ width: `${progress.total > 0 ? (progress.progress / progress.total) * 100 : 0}%` }}
                                                            />
                                                        </div>
                                                        <span className="task-progress-text">
                                                            {progress.progress}/{progress.total}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Action Buttons */}
                        <div className="workflow-actions">
                            {isRunning && onStop && (
                                <button
                                    className="workflow-action-btn stop"
                                    onClick={onStop}
                                    title="Stop workflow"
                                >
                                    <StopCircle size={14} />
                                    Stop
                                </button>
                            )}
                            {(isComplete || isStopped) && onClear && (
                                <button
                                    className="workflow-action-btn dismiss"
                                    onClick={onClear}
                                    title="Dismiss"
                                >
                                    <X size={14} />
                                    Dismiss
                                </button>
                            )}
                        </div>

                        {/* Status Messages */}
                        {isComplete && (
                            <div className="workflow-success">
                                <CheckCircle size={16} />
                                <span>Workflow completed successfully!</span>
                            </div>
                        )}

                        {isStopped && (
                            <div className="workflow-stopped">
                                <AlertCircle size={16} />
                                <span>Workflow stopped at step {(currentStep || 0) + 1}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default WorkflowTracker;
